<template>
  <div class="maintenance-list">
    <!-- Header with Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <h2 class="text-2xl font-bold text-gray-800">Maintenance Records</h2>
      <div class="flex space-x-2">
        <button @click="showFilters = !showFilters" class="btn-secondary">
          {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
        </button>
        <button @click="handleAddMaintenance" class="btn-primary">Schedule Maintenance</button>
      </div>
    </div>

    <!-- Stats Overview -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <StatsCard
        title="Total"
        :value="maintenanceStore.maintenance.length"
        icon="🛠️"
        trend="total"
      />
      <StatsCard
        title="Scheduled"
        :value="maintenanceStore.scheduledMaintenance.length"
        icon="📅"
        trend="scheduled"
      />
      <StatsCard
        title="Repairs"
        :value="maintenanceStore.repairMaintenance.length"
        icon="🔧"
        trend="repairs"
      />
      <StatsCard
        title="Total Cost"
        :value="formatCurrency(maintenanceStore.totalMaintenanceCost)"
        icon="💰"
        trend="cost"
      />
    </div>

    <!-- Filters -->
    <div v-if="showFilters" class="mb-6">
      <MaintenanceFilters @filter="handleFilter" />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <LoadingSpinner class="mx-auto" />
      <p class="text-gray-600 mt-2">Loading maintenance records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div class="flex items-center">
        <span class="text-red-600">⚠️</span>
        <p class="text-red-800 ml-2">{{ error }}</p>
      </div>
      <button @click="fetchMaintenance" class="btn-secondary mt-2">Try Again</button>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredMaintenance.length === 0"
      class="text-center py-12 bg-gray-50 rounded-lg"
    >
      <p class="text-gray-600 mb-4">No maintenance records found</p>
      <button @click="handleAddMaintenance" class="btn-primary">Schedule First Maintenance</button>
    </div>

    <!-- Maintenance Grid/Table -->
    <div v-else>
      <!-- Toggle View -->
      <div class="flex justify-end mb-4">
        <div class="flex border border-gray-300 rounded-md overflow-hidden">
          <button
            @click="viewMode = 'grid'"
            class="px-3 py-2 text-sm"
            :class="viewMode === 'grid' ? 'bg-blue-100 text-blue-800' : 'bg-white text-gray-700'"
          >
            Grid View
          </button>
          <button
            @click="viewMode = 'table'"
            class="px-3 py-2 text-sm"
            :class="viewMode === 'table' ? 'bg-blue-100 text-blue-800' : 'bg-white text-gray-700'"
          >
            Table View
          </button>
        </div>
      </div>

      <!-- Grid View -->
      <div v-if="viewMode === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MaintenanceCard
          v-for="item in paginatedMaintenance"
          :key="item.id"
          :maintenance="item"
          @edit="handleEdit"
          @view="handleView"
          @delete="handleDelete"
        />
      </div>

      <!-- Table View -->
      <div v-else class="bg-white rounded-lg shadow overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Equipment
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Type
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Description
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Cost
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="item in paginatedMaintenance" :key="item.id">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">{{ item.equipment?.name }}</div>
                <div class="text-sm text-gray-500">{{ item.equipment?.type }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <MaintenanceTypeBadge :type="item.maintenance_type" />
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-900">{{ item.description }}</div>
                <div v-if="item.technician" class="text-sm text-gray-500">
                  Tech: {{ item.technician }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">{{ formatDate(item.maintenance_date) }}</div>
                <div v-if="item.next_due_date" class="text-xs text-blue-600">
                  Next: {{ formatDate(item.next_due_date) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  {{ formatCurrency(item.cost) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button @click="handleEdit(item)" class="text-blue-600 hover:text-blue-900 mr-3">
                  Edit
                </button>
                <button @click="handleView(item)" class="text-green-600 hover:text-green-900 mr-3">
                  View
                </button>
                <button @click="handleDelete(item.id)" class="text-red-600 hover:text-red-900">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="mt-6 flex justify-between items-center">
        <div class="text-sm text-gray-700">
          Showing {{ filteredMaintenance.length }} of
          {{ maintenanceStore.maintenance.length }} records
        </div>
        <Pagination
          :current-page="currentPage"
          :total-pages="totalPages"
          @page-change="handlePageChange"
        />
      </div>
    </div>

    <!-- Maintenance Form Modal -->
    <MaintenanceForm
      v-if="showForm"
      :maintenance="editingMaintenance || undefined"
      :loading="formLoading"
      @save="handleSave"
      @cancel="showForm = false"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Delete Maintenance Record"
      message="Are you sure you want to delete this maintenance record? This action cannot be undone."
      @confirm="confirmDelete"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useMaintenanceStore } from '@/stores/maintenanceStore'
import { useEquipmentStore } from '@/stores/equipmentStore'
import type { Maintenance, MaintenanceCreate } from '@/types/maintenance'
import MaintenanceCard from './MaintenanceCard.vue'
import MaintenanceFilters from './MaintenanceFilters.vue'
import MaintenanceForm from './MaintenanceForm.vue'
import MaintenanceTypeBadge from './MaintenanceTypeBadge.vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import StatsCard from '@/components/shared/StatsCard.vue'
import Pagination from '@/components/shared/Pagination.vue'

const maintenanceStore = useMaintenanceStore()
const equipmentStore = useEquipmentStore()

const showForm = ref(false)
const showFilters = ref(false)
const showDeleteConfirm = ref(false)
const viewMode = ref<'grid' | 'table'>('grid')
const editingMaintenance = ref<Maintenance | null>(null)
const deletingMaintenanceId = ref<number | null>(null)
const formLoading = ref(false)
const currentFilters = ref<Record<string, unknown>>({})
const currentPage = ref(1)
const itemsPerPage = ref(12)

const { maintenance, loading, error } = storeToRefs(maintenanceStore)
const { maintenanceCount } = storeToRefs(maintenanceStore)

// Fetch maintenance from backend with pagination
const fetchMaintenance = () => {
  maintenanceStore.fetchMaintenance({ page: currentPage.value, page_size: itemsPerPage.value })
}

onMounted(() => {
  equipmentStore.fetchEquipment()
  fetchMaintenance()
})

// Watch for page changes to fetch new data
watch(currentPage, () => {
  fetchMaintenance()
})

// Remove local .slice() pagination, use backend data directly
const paginatedMaintenance = computed(() => maintenance.value)
const totalPages = computed(() => Math.ceil(maintenanceCount.value / itemsPerPage.value))

// Filter maintenance based on current filters
const filteredMaintenance = computed(() => {
  // Ensure maintenance.value is an array (API may sometimes return unexpected shapes)
  let filtered = Array.isArray(maintenance.value) ? maintenance.value : []

  const filters = currentFilters.value || ({} as Record<string, unknown>)

  // maintenance_type filter
  const maintenanceType =
    typeof filters.maintenance_type === 'string' ? filters.maintenance_type : undefined
  if (maintenanceType) {
    filtered = filtered.filter((m) => m.maintenance_type === maintenanceType)
  }

  // equipment_id filter (accepts string or number)
  if (filters.equipment_id !== undefined && filters.equipment_id !== null) {
    const raw = filters.equipment_id
    const eqId = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw)
    if (!Number.isNaN(eqId)) {
      filtered = filtered.filter((m) => m.equipment_id === eqId)
    }
  }

  // date range filter
  if (typeof filters.start_date === 'string' && typeof filters.end_date === 'string') {
    const start = new Date(filters.start_date)
    const end = new Date(filters.end_date)
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      filtered = filtered.filter((m) => {
        const date = new Date(m.maintenance_date)
        return date >= start && date <= end
      })
    }
  }

  // cost range filter (format: 'min-max' or 'min')
  if (typeof filters.cost_range === 'string' && filters.cost_range.trim() !== '') {
    const parts = filters.cost_range.split('-').map((s) => Number(s.trim()))
    const min = Number.isFinite(parts[0]) ? parts[0] : 0
    const max = parts.length > 1 && Number.isFinite(parts[1]) ? parts[1] : undefined
    if (typeof max === 'number') {
      filtered = filtered.filter((m) => {
        const c = typeof m.cost === 'number' ? m.cost : Number(m.cost)
        return Number.isFinite(c) && c >= min && c <= (max as number)
      })
    } else {
      filtered = filtered.filter((m) => {
        const c = typeof m.cost === 'number' ? m.cost : Number(m.cost)
        return Number.isFinite(c) && c >= min
      })
    }
  }

  return filtered
})

import { formatCurrencyMaybe } from '@/utils/format'

const formatCurrency = formatCurrencyMaybe

const handleFilter = (filters: Record<string, unknown>) => {
  currentFilters.value = filters
  currentPage.value = 1 // Reset to first page when filters change
}

const handleAddMaintenance = () => {
  editingMaintenance.value = null
  showForm.value = true
}

const router = useRouter()

const handleEdit = (maintenanceItem: Maintenance) => {
  editingMaintenance.value = maintenanceItem
  showForm.value = true
}

const handleView = (maintenanceItem: Maintenance) => {
  if (maintenanceItem && maintenanceItem.id != null) {
    router.push({ name: 'MaintenanceDetail', params: { id: String(maintenanceItem.id) } })
  }
}

const handleDelete = (id: number) => {
  deletingMaintenanceId.value = id
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  if (deletingMaintenanceId.value != null) {
    try {
      await maintenanceStore.deleteMaintenance(deletingMaintenanceId.value)
      showDeleteConfirm.value = false
      deletingMaintenanceId.value = null
      // Re-fetch current page to ensure pagination and counts are accurate
      fetchMaintenance()
    } catch (err) {
      console.error('Failed to delete maintenance:', err)
    }
  }
}

const handleSave = async (data: MaintenanceCreate) => {
  formLoading.value = true
  try {
    if (editingMaintenance.value) {
      await maintenanceStore.updateMaintenance(editingMaintenance.value.id, data)
    } else {
      await maintenanceStore.createMaintenance(data)
    }
    showForm.value = false
    editingMaintenance.value = null
  } catch (err) {
    console.error('Failed to save maintenance:', err)
  } finally {
    formLoading.value = false
  }
}

const handlePageChange = (page: number) => {
  currentPage.value = page
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}
</script>
